import React from 'react';
import clsx from 'clsx';
import {
  ArrowTopRightOnSquareIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import type { ToolResultBlock, ToolUseBlock } from '../../../store/chatStore';
import { safeJsonParse } from '../../../../../utils/jsonRepairUtils';
import { Image } from '../../../../ui/Image';
import {
  PleriActionCell,
  type MoreOption,
  type RemediationConfig,
} from './Table/components/PleriActionCell';
import type { GenericItem } from './Table/types';
import {
  isRecord,
  getString,
  getFirstString,
  splitByAssetName,
} from './shared/fieldUtils';
import { getResourceImageFallback } from './shared/imageFallback';
import type { TFunction } from 'i18next';

import { useTranslation } from 'react-i18next';

interface Props {
  result: ToolResultBlock;
  toolUse: ToolUseBlock;
}

type ChipTone = 'neutral' | 'info' | 'warning' | 'good' | 'bad';

interface ChipProps {
  label: string;
  tone?: ChipTone;
  size?: 'default' | 'compact';
}

const toneClasses: Record<ChipTone, string> = {
  neutral: 'bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100',
  info: 'bg-sky-100 dark:bg-sky-900 text-sky-900 dark:text-sky-200',
  warning: 'bg-amber-100 dark:bg-amber-900 text-amber-900 dark:text-amber-200',
  good: 'bg-emerald-100 dark:bg-emerald-900 text-emerald-900 dark:text-emerald-200',
  bad: 'bg-rose-100 dark:bg-rose-900 text-rose-900 dark:text-rose-200',
};

/* ── Risk score bar colours (mirrored from risk/src/colors.css) ────── */
const SEVERITY_COLORS = {
  critical: { main: '#B91C1C', muted: '#F1D2D2', border: '#B91C1C' },
  high: { main: '#DF3232', muted: '#FCEAEA', border: '#DF3232' },
  medium: { main: '#FF5400', muted: '#FFEEE5', border: '#FF5400' },
  low: { main: '#593922', muted: '#E2D2C4', border: '#593922' },
} as const;

type SeverityKey = keyof typeof SEVERITY_COLORS;

const SEGMENT_COUNT = 10;
const INACTIVE_SEGMENT_COLOR = '#ACACAC'; // darker-gray (matches design-system tailwind-theme)

/** Normalise a raw risk score (0-25) to a 0-10 scale. */
function normaliseScore(rating: number, maxRating = 25): number {
  return (rating / maxRating) * 10;
}

/** Map a raw risk score (0-25) to a severity key. */
function getSeverityKey(rating: number): SeverityKey {
  const score = normaliseScore(rating);
  const range = 10 / 4; // 2.5
  if (score <= range) {
    return 'low';
  }
  if (score <= range * 2) {
    return 'medium';
  }
  if (score <= range * 3) {
    return 'high';
  }
  return 'critical';
}

/** Parse a value to a finite number, or return undefined. */
function getNumericScore(value: unknown): number | undefined {
  const raw = getString(value);
  if (!raw) {
    return undefined;
  }
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : undefined;
}

/** Vertical segmented bar identical to the one in risk/src/atoms/RatingBar. */
const RiskScoreBar: React.FC<{ rating: number }> = ({ rating }) => {
  const clamped = Math.max(rating, 2.5);
  const filled = Math.round(normaliseScore(clamped));
  const severity = getSeverityKey(rating);
  const { main } = SEVERITY_COLORS[severity];

  return (
    <div className="flex flex-col gap-[3px]">
      {Array.from({ length: SEGMENT_COUNT }, (_, i) => {
        const highlighted = i >= SEGMENT_COUNT - filled;
        return (
          <div
            key={i}
            className={clsx(
              'h-3.5 w-2.5',
              i === 0 && 'rounded-t-md',
              i === SEGMENT_COUNT - 1 && 'rounded-b-md',
            )}
            style={{
              backgroundColor: highlighted ? main : INACTIVE_SEGMENT_COLOR,
            }}
          />
        );
      })}
    </div>
  );
};

const riskMoreOptions = (t: TFunction<'pleri/chat'>): MoreOption[] => [
  {
    label: t('suggestFix'),
    message: 'Suggest fix for this risk',
  },
  {
    label: t('createTicket'),
    message: 'Can you help me create a ticket for this?',
  },
  {
    label: t('exempt'),
    message: 'Exempt this risk.',
  },
];

const riskInvestigateConfig = (
  t: TFunction<'pleri/chat'>,
): RemediationConfig => ({
  tooltip: t('investigate'),
  message: 'Investigate this risk',
  icon: MagnifyingGlassIcon,
  hoverClasses:
    'hover:bg-orange-100 hover:text-orange-700 hover:ring-orange-300',
});

const Chip: React.FC<ChipProps> = ({
  label,
  tone = 'neutral',
  size = 'default',
}) => {
  return (
    <span
      className={clsx(
        'inline-flex max-w-full items-center rounded-md font-normal',
        size === 'compact' ? 'px-2 py-0.5 text-xs' : 'px-2 py-1 text-xs',
        toneClasses[tone],
      )}
    >
      <span className="truncate">{label}</span>
    </span>
  );
};

function parseRisks(content: unknown): Record<string, unknown>[] {
  const parsed =
    typeof content === 'string'
      ? safeJsonParse<unknown>(content, 'listRisks result')
      : content;

  if (Array.isArray(parsed)) {
    return parsed.filter(isRecord);
  }

  if (!isRecord(parsed) || !Array.isArray(parsed.data)) {
    return [];
  }

  return parsed.data.filter(isRecord);
}

function toTitleCase(value: string): string {
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatNumber(value: unknown): string | undefined {
  const raw = getString(value);
  if (!raw) {
    return undefined;
  }
  const numeric = Number.parseFloat(raw);
  if (Number.isNaN(numeric)) {
    return raw;
  }
  return numeric.toFixed(1);
}

function getSeverityChip(value: unknown): {
  label: string;
  classes: string;
} | null {
  const severity = getString(value)?.trim();
  if (!severity) {
    return null;
  }

  const normalized = severity.toLowerCase();
  if (normalized === 'critical' || normalized === 'high') {
    if (normalized === 'critical') {
      return {
        label: toTitleCase(severity),
        classes: 'bg-severity-critical-background text-severity-critical-base',
      };
    }
    return {
      label: toTitleCase(severity),
      classes: 'bg-severity-high-background text-severity-high-base',
    };
  }
  if (normalized === 'medium') {
    return {
      label: toTitleCase(severity),
      classes: 'bg-severity-medium-background text-severity-medium-base',
    };
  }
  if (normalized === 'low') {
    return {
      label: toTitleCase(severity),
      classes: 'bg-severity-low-background text-severity-low-base',
    };
  }
  if (normalized === 'info' || normalized === 'informational') {
    return {
      label: toTitleCase(severity),
      classes:
        'bg-severity-informational-background text-severity-informational-base',
    };
  }

  return {
    label: toTitleCase(severity),
    classes: 'bg-severity-unknown-background text-severity-unknown-base',
  };
}

function getMetaRecord(risk: Record<string, unknown>): Record<string, unknown> {
  if (isRecord(risk.meta)) {
    return risk.meta;
  }
  return {};
}

function getResourceType(risk: Record<string, unknown>): string | undefined {
  return (
    getString(risk.resourceType) || getString(getMetaRecord(risk).resourceType)
  );
}

function getPrimaryAssetId(risk: Record<string, unknown>): string | undefined {
  return getString(risk.primaryAssetId) || getString(risk.assetId);
}

export const RisksTableRenderer: React.FC<Props> = ({ result }) => {
  const { t } = useTranslation('pleri/chat');
  if (result.isError) {
    return (
      <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950">
        {typeof result.content === 'string'
          ? result.content
          : JSON.stringify(result.content, null, 2)}
      </div>
    );
  }

  const risks = parseRisks(result.content);
  if (risks.length === 0) {
    return (
      <div className="py-2 text-sm text-slate-600 dark:text-slate-400">
        {t('noRisksFound')}
      </div>
    );
  }

  return (
    <div className="space-y-4 font-sans whitespace-normal">
      {risks.map((risk, index) => {
        const riskId = getString(risk.id) ?? `risk-${index}`;
        const description = getString(risk.description) || t('untitledRisk');
        const meta = getMetaRecord(risk);
        const assetName = getFirstString(
          risk.assetName,
          risk.asset_name,
          meta.assetName,
          meta.asset_name,
          risk.resourceName,
          risk.resource_name,
          meta.resourceName,
          meta.resource_name,
        );
        const descriptionParts = splitByAssetName(description, assetName);
        const descriptionContent = descriptionParts ? (
          <>
            {descriptionParts.before}
            <strong className="font-semibold">{descriptionParts.match}</strong>
            {descriptionParts.after}
          </>
        ) : (
          description
        );
        const severity = getSeverityChip(risk.severityLevel);
        const score = formatNumber(risk.score);
        const rawScore = getNumericScore(risk.score);
        const likelihood = formatNumber(risk.likelihood);
        const impact = formatNumber(risk.impact);
        const resourceType = getResourceType(risk) ?? 'resource';
        const provider = getFirstString(
          risk.provider,
          risk.provider_name,
          meta.provider,
          meta.provider_name,
        );

        const assetIdentifier = getPrimaryAssetId(risk);
        const rawRiskId = getString(risk.id);
        const riskDetailUrl = rawRiskId
          ? `/risks/${btoa(rawRiskId)}`
          : undefined;
        const severityKey =
          rawScore != null ? getSeverityKey(rawScore) : undefined;
        const sidebarColors = severityKey
          ? SEVERITY_COLORS[severityKey]
          : undefined;

        return (
          <article
            key={riskId}
            className="group/asset-card relative flex overflow-visible rounded-xl border border-slate-200 dark:border-slate-700 bg-white shadow-sm transition-shadow hover:shadow-md dark:bg-gray-900"
          >
            {/* ── Score sidebar ──────────────────────────── */}
            {rawScore != null && sidebarColors && (
              <div
                className="flex w-14 shrink-0 items-center justify-center rounded-l-[11px] border-r py-4"
                style={{
                  backgroundColor: sidebarColors.muted,
                  borderColor: sidebarColors.border,
                }}
              >
                <RiskScoreBar rating={rawScore} />
              </div>
            )}
            {/* ── Card body ─────────────────────────────── */}
            <div className="relative flex min-h-[100px] min-w-0 flex-1 flex-col justify-between p-4 pr-32">
              <PleriActionCell
                row={{
                  original: {
                    ...(risk as GenericItem),
                    riskId,
                    assetId: assetIdentifier ?? '',
                  },
                }}
                showRemediationButton
                remediationConfig={riskInvestigateConfig(t)}
                moreOptions={riskMoreOptions(t)}
                position="right"
                hoverScope="asset-card"
                alwaysVisible
                size="compact"
                direction="vertical"
              />

              {/* Top: icon + severity, then description */}
              <div>
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 flex-shrink-0 overflow-hidden rounded-md bg-slate-100 dark:bg-slate-700">
                    <Image
                      type="resourceType"
                      name={resourceType}
                      fallback={getResourceImageFallback({
                        provider,
                        resourceType,
                      })}
                      className="h-full w-full object-contain p-0.5"
                    />
                  </div>
                  {severity && (
                    <span
                      className={clsx(
                        'inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-xs font-semibold',
                        severity.classes,
                      )}
                      title={t('severity')}
                    >
                      {severity.label}
                    </span>
                  )}
                </div>

                <div
                  className="mt-2 min-w-0 text-sm break-words whitespace-normal text-slate-800 dark:text-slate-200"
                  title={t('riskDescription', { description })}
                >
                  {riskDetailUrl ? (
                    <a
                      href={riskDetailUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-slate-800 hover:text-slate-900 dark:text-slate-200 dark:hover:text-slate-100"
                    >
                      {descriptionContent}
                      <span className="ml-1 inline-flex h-3.5 w-3.5 translate-y-[1px] items-center justify-center rounded bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-300">
                        <ArrowTopRightOnSquareIcon className="h-2 w-2" />
                      </span>
                    </a>
                  ) : (
                    descriptionContent
                  )}
                </div>
              </div>

              {/* Bottom: chips */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
                <div className="mt-2 flex items-center gap-2">
                  {score && (
                    <Chip label={t('scoreValue', { score })} size="compact" />
                  )}
                  {likelihood && (
                    <Chip
                      label={t('likelihood', { likelihood })}
                      size="compact"
                    />
                  )}
                  {impact && (
                    <Chip label={t('impactValue', { impact })} size="compact" />
                  )}
                </div>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
};
